import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Avatar,
  Rating,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Chip,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { ITestimonial } from "../types";

interface TestimonialCardProps {
  item: ITestimonial;
  onView: (item: ITestimonial) => void;
  onEdit: (item: ITestimonial) => void;
  onDelete: (item: ITestimonial) => void;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  item,
  onView,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const reviewerName =
    item.reviewerName || item.name || item.author || "Anonymous";
  const commentText =
    item.comment || item.review || item.message || "No comment provided.";
  const ratingValue = Number(item.rating ?? 5);
  const isVerified = Boolean(item.verifiedBuyer);
  const tag = item.tag || "";
  const imageUrl =
    item.image?.url ||
    (typeof item.image === "string" ? item.image : null) ||
    item.avatar;

  const dateString = item.createdAt || item.created_at || item.date || "";
  const formattedDate = dateString
    ? new Date(dateString).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  return (
    <Card
      sx={{
        p: 2.5,
        borderRadius: 2,
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        transition: "all 0.25s ease-in-out",
        "&:hover": {
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          transform: "translateY(-3px)",
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
      }}
    >
      <Box>
        {/* Top Header: Avatar, Reviewer Name, Verified Badge, Rating & 3-dots Menu */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1.5}
          mb={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={imageUrl}
              alt={reviewerName}
              sx={{
                width: 46,
                height: 46,
                bgcolor: theme.palette.primary.main,
                fontWeight: 700,
                fontSize: "1.1rem",
              }}
            >
              {reviewerName.charAt(0).toUpperCase()}
            </Avatar>

            <Box>
              <Stack direction="row" alignItems="center" spacing={0.6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.3,
                  }}
                >
                  {reviewerName}
                </Typography>
                {isVerified && (
                  <Icon
                    icon="solar:check-circle-bold"
                    width={16}
                    style={{ color: theme.palette.success.main }}
                  />
                )}
              </Stack>

              {isVerified ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.success.main,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                  }}
                >
                  Verified Buyer
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.72rem" }}
                >
                  Reviewer
                </Typography>
              )}
            </Box>
          </Stack>

          <IconButton
            size="small"
            onClick={handleOpenMenu}
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Icon icon="solar:menu-dots-bold" width={20} />
          </IconButton>
        </Stack>

        {/* Rating & Tag */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          mb={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={0.8}>
            <Rating
              value={ratingValue}
              readOnly
              precision={0.5}
              size="small"
              sx={{
                color: "#faaf00",
                "& .MuiRating-iconFilled": { color: "#faaf00" },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: theme.palette.warning.dark,
              }}
            >
              {ratingValue.toFixed(1)}
            </Typography>
          </Stack>

          {tag && (
            <Chip
              label={tag}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                fontWeight: 600,
                fontSize: "0.72rem",
                height: 22,
              }}
            />
          )}
        </Stack>

        {/* Comment Quote / Text */}
        <Box
          sx={{
            position: "relative",
            minHeight: 55,
            cursor: "pointer",
          }}
          onClick={() => onView(item)}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontStyle: "italic",
            }}
          >
            "{commentText}"
          </Typography>
        </Box>
      </Box>

      {/* Card Footer: Date & View Details Link */}
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="caption" color="text.disabled">
            {formattedDate || "Recent"}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => onView(item)}
          >
            View Details &rarr;
          </Typography>
        </Stack>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            minWidth: 150,
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            borderRadius: 1.5,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            onView(item);
          }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: theme.palette.info.main }}>
            <Icon icon="solar:eye-bold" width={18} />
          </ListItemIcon>
          <ListItemText
            primary="View Details"
            primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseMenu();
            onEdit(item);
          }}
        >
          <ListItemIcon
            sx={{ minWidth: 28, color: theme.palette.primary.main }}
          >
            <Icon icon="solar:pen-bold" width={18} />
          </ListItemIcon>
          <ListItemText
            primary="Edit"
            primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseMenu();
            onDelete(item);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: "error.main" }}>
            <Icon icon="solar:trash-bin-trash-bold" width={18} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            primaryTypographyProps={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: "error.main",
            }}
          />
        </MenuItem>
      </Menu>
    </Card>
  );
};

export { TestimonialCard };
